import { forwardRef } from "react";
import { SvgMultilineText, splitLabel } from "./visual-utils";

type BiologyTemplate = "digestive-system" | "human-body" | "leaf" | "plant";

type BiologyData = {
  template?: BiologyTemplate;
  title?: string;
  width?: number;
  height?: number;
  highlight?: string[];
  labels?: Record<string, string>;
  notes?: string[];
};

type PartDefinition = {
  key: string;
  label: string;
  anchor: { x: number; y: number };
  labelPosition: { x: number; y: number };
  render: (style: { fill: string; stroke: string; strokeWidth?: number }) => JSX.Element;
};

const neutralFill = "hsl(var(--muted))";
const neutralStroke = "hsl(var(--border))";
const anatomyFill = "hsl(var(--secondary) / 0.24)";
const anatomyStroke = "hsl(var(--secondary))";
const plantFill = "hsl(var(--success) / 0.24)";
const plantStroke = "hsl(var(--success))";
const labelFill = "hsl(var(--card))";
const labelStroke = "hsl(var(--border))";
const labelText = "hsl(var(--foreground))";
const connectorColor = "hsl(var(--muted-foreground) / 0.7)";
const silhouetteFill = "hsl(var(--background))";

function renderCallout(part: PartDefinition, label: string) {
  const lines = splitLabel(label, 15, 2);
  const boxWidth = 116;
  const boxHeight = Math.max(30, 16 + lines.length * 14);
  const x = part.labelPosition.x - boxWidth / 2;
  const y = part.labelPosition.y - boxHeight / 2;

  return (
    <g key={`${part.key}-label`}>
      <line
        x1={part.anchor.x}
        y1={part.anchor.y}
        x2={part.labelPosition.x}
        y2={part.labelPosition.y}
        stroke={connectorColor}
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />
      <rect x={x} y={y} width={boxWidth} height={boxHeight} rx="14" fill={labelFill} stroke={labelStroke} />
      <SvgMultilineText x={part.labelPosition.x} y={part.labelPosition.y} label={label} fill={labelText} fontSize={11} maxCharsPerLine={15} />
    </g>
  );
}

function getDigestiveTemplate(): PartDefinition[] {
  return [
    {
      key: "mouth",
      label: "Bouche",
      anchor: { x: 220, y: 48 },
      labelPosition: { x: 86, y: 52 },
      render: ({ fill, stroke }) => <ellipse cx="220" cy="48" rx="18" ry="11" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "esophagus",
      label: "Œsophage",
      anchor: { x: 220, y: 118 },
      labelPosition: { x: 88, y: 122 },
      render: ({ fill, stroke }) => <rect x="213" y="60" width="14" height="120" rx="7" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "liver",
      label: "Foie",
      anchor: { x: 282, y: 170 },
      labelPosition: { x: 358, y: 126 },
      render: ({ fill, stroke }) => <path d="M248 142 C292 132, 328 142, 330 176 C328 200, 292 210, 252 200 C240 186, 240 162, 248 142 Z" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "stomach",
      label: "Estomac",
      anchor: { x: 198, y: 188 },
      labelPosition: { x: 88, y: 196 },
      render: ({ fill, stroke }) => <path d="M214 142 C184 146, 174 170, 178 196 C182 220, 204 234, 220 230 C238 226, 246 210, 242 194 C236 168, 240 152, 214 142 Z" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "small_intestine",
      label: "Intestin grêle",
      anchor: { x: 220, y: 308 },
      labelPosition: { x: 86, y: 314 },
      render: ({ fill, stroke }) => <path d="M182 248 C214 232, 254 236, 276 262 C290 280, 288 308, 270 326 C250 348, 212 352, 188 336 C164 320, 158 282, 182 248 Z M196 268 C212 252, 242 254, 254 274 C264 290, 254 314, 236 320 C216 328, 194 316, 188 298 C182 288, 184 276, 196 268 Z" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "large_intestine",
      label: "Gros intestin",
      anchor: { x: 290, y: 286 },
      labelPosition: { x: 360, y: 292 },
      render: ({ stroke }) => <path d="M164 236 C150 248, 148 344, 176 356 L268 356 C296 344, 302 248, 286 236" fill="none" stroke={stroke} strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />,
    },
  ];
}

function getHumanTemplate(): PartDefinition[] {
  return [
    {
      key: "brain",
      label: "Cerveau",
      anchor: { x: 220, y: 64 },
      labelPosition: { x: 92, y: 60 },
      render: ({ fill, stroke }) => <path d="M198 52 C198 36, 214 28, 228 32 C246 28, 260 42, 258 60 C266 72, 258 88, 242 90 C230 100, 208 98, 196 86 C182 80, 182 62, 198 52 Z" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "lungs",
      label: "Poumons",
      anchor: { x: 220, y: 166 },
      labelPosition: { x: 94, y: 162 },
      render: ({ fill, stroke }) => (
        <g>
          <path d="M184 110 C162 118, 150 144, 154 176 C158 212, 182 232, 206 226 C214 190, 214 142, 206 110 Z" fill={fill} stroke={stroke} strokeWidth="2" />
          <path d="M256 110 C278 118, 290 144, 286 176 C282 212, 258 232, 234 226 C226 190, 226 142, 234 110 Z" fill={fill} stroke={stroke} strokeWidth="2" />
        </g>
      ),
    },
    {
      key: "heart",
      label: "Cœur",
      anchor: { x: 220, y: 192 },
      labelPosition: { x: 354, y: 170 },
      render: ({ fill, stroke }) => <path d="M220 212 C246 198, 254 180, 246 166 C238 152, 222 156, 220 170 C218 156, 202 152, 194 166 C186 180, 194 198, 220 212 Z" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "liver",
      label: "Foie",
      anchor: { x: 270, y: 234 },
      labelPosition: { x: 352, y: 226 },
      render: ({ fill, stroke }) => <path d="M240 214 C274 206, 304 214, 308 236 C300 256, 270 264, 242 254 C232 244, 232 226, 240 214 Z" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "stomach",
      label: "Estomac",
      anchor: { x: 192, y: 236 },
      labelPosition: { x: 92, y: 234 },
      render: ({ fill, stroke }) => <path d="M212 206 C188 210, 180 228, 184 248 C188 266, 202 278, 216 274 C230 270, 236 252, 232 238 C226 222, 232 212, 212 206 Z" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "intestines",
      label: "Intestins",
      anchor: { x: 220, y: 308 },
      labelPosition: { x: 354, y: 306 },
      render: ({ fill, stroke }) => <path d="M184 270 C200 256, 240 256, 258 272 C272 284, 274 314, 256 330 C238 346, 202 346, 184 330 C168 314, 168 286, 184 270 Z" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
  ];
}

function getLeafTemplate(): PartDefinition[] {
  return [
    {
      key: "blade",
      label: "Limbe",
      anchor: { x: 210, y: 184 },
      labelPosition: { x: 90, y: 120 },
      render: ({ fill, stroke }) => <path d="M210 40 C132 70, 86 142, 94 232 C104 322, 162 378, 210 392 C258 378, 316 322, 326 232 C334 142, 288 70, 210 40 Z" fill={fill} stroke={stroke} strokeWidth="3" />,
    },
    {
      key: "midrib",
      label: "Nervure principale",
      anchor: { x: 210, y: 228 },
      labelPosition: { x: 344, y: 144 },
      render: ({ stroke }) => <line x1="210" y1="52" x2="210" y2="392" stroke={stroke} strokeWidth="5" strokeLinecap="round" />,
    },
    {
      key: "veins",
      label: "Nervures secondaires",
      anchor: { x: 256, y: 206 },
      labelPosition: { x: 350, y: 236 },
      render: ({ stroke }) => (
        <g>
          <path d="M210 118 L142 160" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path d="M210 156 L136 210" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path d="M210 202 L144 252" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path d="M210 118 L278 160" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path d="M210 156 L286 210" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path d="M210 202 L276 252" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
        </g>
      ),
    },
    {
      key: "petiole",
      label: "Pétiole",
      anchor: { x: 210, y: 406 },
      labelPosition: { x: 92, y: 372 },
      render: ({ fill, stroke }) => <rect x="200" y="392" width="20" height="52" rx="10" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "apex",
      label: "Sommet",
      anchor: { x: 210, y: 42 },
      labelPosition: { x: 332, y: 56 },
      render: () => <circle cx="210" cy="42" r="7" fill="hsl(var(--accent))" stroke="hsl(var(--accent-foreground))" strokeWidth="1.5" />,
    },
  ];
}

function getPlantTemplate(): PartDefinition[] {
  return [
    {
      key: "flower",
      label: "Fleur",
      anchor: { x: 210, y: 86 },
      labelPosition: { x: 90, y: 92 },
      render: ({ fill, stroke }) => (
        <g>
          <circle cx="210" cy="86" r="18" fill={fill} stroke={stroke} strokeWidth="2" />
          <circle cx="184" cy="86" r="16" fill={fill} stroke={stroke} strokeWidth="2" />
          <circle cx="236" cy="86" r="16" fill={fill} stroke={stroke} strokeWidth="2" />
          <circle cx="210" cy="60" r="16" fill={fill} stroke={stroke} strokeWidth="2" />
          <circle cx="210" cy="112" r="16" fill={fill} stroke={stroke} strokeWidth="2" />
        </g>
      ),
    },
    {
      key: "stem",
      label: "Tige",
      anchor: { x: 210, y: 254 },
      labelPosition: { x: 94, y: 252 },
      render: ({ fill, stroke }) => <rect x="202" y="118" width="16" height="228" rx="8" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "leaves",
      label: "Feuilles",
      anchor: { x: 170, y: 202 },
      labelPosition: { x: 340, y: 168 },
      render: ({ fill, stroke }) => (
        <g>
          <path d="M204 176 C164 146, 130 156, 114 196 C142 216, 176 216, 204 190 Z" fill={fill} stroke={stroke} strokeWidth="2" />
          <path d="M216 210 C246 180, 286 182, 312 220 C286 236, 246 236, 216 220 Z" fill={fill} stroke={stroke} strokeWidth="2" />
        </g>
      ),
    },
    {
      key: "fruit",
      label: "Fruit",
      anchor: { x: 248, y: 250 },
      labelPosition: { x: 342, y: 252 },
      render: ({ fill, stroke }) => <circle cx="248" cy="250" r="20" fill={fill} stroke={stroke} strokeWidth="2" />,
    },
    {
      key: "roots",
      label: "Racines",
      anchor: { x: 210, y: 396 },
      labelPosition: { x: 92, y: 422 },
      render: ({ stroke }) => (
        <g>
          <path d="M210 346 C192 366, 188 396, 170 428" fill="none" stroke={stroke} strokeWidth="4" strokeLinecap="round" />
          <path d="M210 346 C226 368, 234 398, 250 432" fill="none" stroke={stroke} strokeWidth="4" strokeLinecap="round" />
          <path d="M202 368 C186 384, 182 404, 164 418" fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
          <path d="M218 372 C238 388, 244 408, 262 424" fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
        </g>
      ),
    },
  ];
}

function getTemplateParts(template: BiologyTemplate) {
  switch (template) {
    case "digestive-system":
      return { parts: getDigestiveTemplate(), width: 440, height: 440, kind: "anatomy" as const };
    case "leaf":
      return { parts: getLeafTemplate(), width: 420, height: 460, kind: "plant" as const };
    case "plant":
      return { parts: getPlantTemplate(), width: 420, height: 470, kind: "plant" as const };
    case "human-body":
    default:
      return { parts: getHumanTemplate(), width: 440, height: 430, kind: "anatomy" as const };
  }
}

function BodySilhouette({ template }: { template: BiologyTemplate }) {
  if (template === "leaf" || template === "plant") return null;

  return template === "digestive-system" ? (
    <path d="M220 26 C196 26, 178 42, 178 68 C178 88, 188 104, 198 114 L198 372 C198 394, 208 408, 220 408 C232 408, 242 394, 242 372 L242 114 C252 104, 262 88, 262 68 C262 42, 244 26, 220 26 Z" fill={silhouetteFill} stroke={neutralStroke} strokeWidth="2" />
  ) : (
    <path d="M220 22 C194 22, 176 40, 176 68 C176 92, 190 106, 200 116 L186 172 C172 212, 170 286, 184 344 L196 400 L214 400 L210 338 L230 338 L226 400 L244 400 L256 344 C270 286, 268 212, 254 172 L240 116 C250 106, 264 92, 264 68 C264 40, 246 22, 220 22 Z" fill={silhouetteFill} stroke={neutralStroke} strokeWidth="2" />
  );
}

export const BiologySVG = forwardRef<HTMLDivElement, { data: BiologyData }>(({ data }, ref) => {
  const template = data.template ?? "human-body";
  const highlighted = new Set((data.highlight ?? []).map((item) => item.toLowerCase()));
  const { parts, width, height, kind } = getTemplateParts(template);
  const fill = kind === "plant" ? plantFill : anatomyFill;
  const stroke = kind === "plant" ? plantStroke : anatomyStroke;
  const notes = Array.isArray(data.notes) ? data.notes.filter(Boolean).slice(0, 3) : [];

  return (
    <div ref={ref} className="my-3 rounded-2xl border border-border bg-card p-3 overflow-x-auto">
      {data.title && <p className="mb-2 text-center text-xs font-bold text-foreground">{data.title}</p>}
      <svg viewBox={`0 0 ${data.width ?? width} ${data.height ?? height}`} className="mx-auto w-full max-w-lg" style={{ maxHeight: 460 }}>
        <BodySilhouette template={template} />
        {parts.map((part) => {
          const isHighlighted = highlighted.size === 0 || highlighted.has(part.key);
          const partStyle = {
            fill: isHighlighted ? fill : neutralFill,
            stroke: isHighlighted ? stroke : neutralStroke,
            strokeWidth: 2,
          };
          const label = data.labels?.[part.key] ?? part.label;

          return (
            <g key={part.key}>
              {part.render(partStyle)}
              {renderCallout(part, label)}
            </g>
          );
        })}
      </svg>
      {notes.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
          {notes.map((note) => (
            <li key={note}>• {note}</li>
          ))}
        </ul>
      )}
    </div>
  );
});

BiologySVG.displayName = "BiologySVG";